import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Render a long list in pages, growing as the user scrolls.
 *
 * A vault-sized library would otherwise mount thousands of rows on every
 * keystroke-driven re-render. True windowing is the textbook answer, but it
 * needs a known row height, and these rows are not uniform — a document with a
 * search snippet and four tags is twice as tall as one without. Rather than
 * force every row to a fixed height for the sake of the maths, this simply
 * renders the first page and appends another whenever a sentinel at the bottom
 * comes into view.
 *
 * The DOM stays small, scrolling stays native, and rows keep whatever height
 * their content needs.
 */
export function useIncrementalList<T>(
  items: T[],
  pageSize = 100,
): {
  /** The slice to render. */
  visible: T[];
  /** True when more items are waiting. */
  hasMore: boolean;
  /** Attach to an element at the end of the list to load the next page. */
  sentinelRef: (node: HTMLElement | null) => void;
  /** Load the next page explicitly, for the fallback button. */
  loadMore: () => void;
} {
  // A new list (different filter, different query) starts from the top again.
  const { limit, sentinelRef, loadMore } = useIncrementalLimit(items, pageSize);
  const hasMore = limit < items.length;
  return { visible: hasMore ? items.slice(0, limit) : items, hasMore, sentinelRef, loadMore };
}

/**
 * The paging behind `useIncrementalList`, for lists that are not one array —
 * the folder tree spreads its rows over many groups. The limit goes back to one
 * page whenever `resetKey` changes.
 */
export function useIncrementalLimit(
  resetKey: unknown,
  pageSize = 100,
): {
  limit: number;
  sentinelRef: (node: HTMLElement | null) => void;
  loadMore: () => void;
} {
  const [limit, setLimit] = useState(pageSize);
  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    setLimit(pageSize);
  }, [resetKey, pageSize]);

  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      observer.current?.disconnect();
      if (!node || typeof IntersectionObserver === 'undefined') return;
      observer.current = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setLimit((c) => c + pageSize);
        }
      });
      observer.current.observe(node);
    },
    [pageSize],
  );

  useEffect(() => () => observer.current?.disconnect(), []);

  const loadMore = useCallback(() => setLimit((c) => c + pageSize), [pageSize]);

  return { limit, sentinelRef, loadMore };
}
